import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/api/users.api';

/**
 * V4.9.5 — Textarea with @-mention autocomplete.
 *
 * Usage drop-in replacement for `<textarea>`:
 *
 *     <MentionTextarea value={text} onChange={setText} placeholder="…" />
 *
 * Behavior:
 *   • Type `@` to open a dropdown of active users.
 *   • Keep typing to filter (matches alias OR name, case-insensitive).
 *   • ↑/↓ arrow keys to navigate. Enter or Tab to insert. Esc to cancel.
 *   • Click an item to insert.
 *   • Inserts `@alias ` (with trailing space) at the cursor.
 *   • Closes on space / newline if no match.
 *
 * Robustness:
 *   • Uses the existing /api/v1/users list (cached 60s by React Query —
 *     no network on every keystroke).
 *   • The dropdown is absolutely positioned and uses portal-like overflow
 *     behavior via z-index, so it never gets clipped by parent overflow.
 *   • Caps suggestions to 8 to keep the dropdown small.
 *   • If alias contains spaces (e.g. "Janani MR") we use the alias word
 *     before the first space for the @-token (Janani), but expose the
 *     full alias as the display label.
 */

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  className?: string;
  id?: string;
}

interface UserOption {
  id: string;
  alias: string;
  name: string;
  /** The token that gets inserted (alias's first whitespace-free segment) */
  token: string;
}

export function MentionTextarea({
  value,
  onChange,
  placeholder,
  rows = 4,
  disabled,
  className,
  id
}: Props) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [dropdown, setDropdown] = useState<{
    open: boolean;
    query: string;
    startIndex: number; // position of the @ in `value`
    activeIndex: number;
  }>({ open: false, query: '', startIndex: -1, activeIndex: 0 });

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    staleTime: 60_000
  });

  const options: UserOption[] = useMemo(() => {
    const rows = usersQuery.data ?? [];
    return rows
      // V4.9.5 — guard against null aliases (legacy user rows), inactive
      // users (admin gets them in the list), and any non-string alias values.
      .filter(
        (u: any) =>
          u &&
          typeof u.alias === 'string' &&
          u.alias.trim().length > 0 &&
          u.active !== false &&
          !u.deleted_at
      )
      .map((u: any) => {
        const aliasStr = String(u.alias).trim();
        const token = aliasStr.split(/\s+/)[0] || aliasStr;
        return {
          id: u.id,
          alias: aliasStr,
          name: typeof u.name === 'string' ? u.name : aliasStr,
          token
        };
      });
  }, [usersQuery.data]);

  const filtered = useMemo(() => {
    if (!dropdown.open) return [] as UserOption[];
    const q = dropdown.query.toLowerCase();
    return options
      .filter(
        (o) =>
          o.token.toLowerCase().startsWith(q) ||
          o.alias.toLowerCase().includes(q) ||
          o.name.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [options, dropdown.open, dropdown.query]);

  // Reset active index when filter changes
  useEffect(() => {
    setDropdown((d) => ({ ...d, activeIndex: 0 }));
  }, [dropdown.query]);

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const next = e.target.value;
    const cursor = e.target.selectionStart ?? next.length;
    // Find a `@` that starts a token at or before the cursor (no space between)
    const before = next.slice(0, cursor);
    const atMatch = /(?:^|\s)@([a-zA-Z0-9_.-]*)$/.exec(before);
    if (atMatch) {
      const startIndex = cursor - atMatch[1].length - 1;
      setDropdown({
        open: true,
        query: atMatch[1],
        startIndex,
        activeIndex: 0
      });
    } else {
      setDropdown((d) => (d.open ? { ...d, open: false } : d));
    }
    onChange(next);
  }

  function insertAlias(opt: UserOption) {
    const el = ref.current;
    if (!el) return;
    const cursor = el.selectionStart ?? value.length;
    const before = value.slice(0, dropdown.startIndex);
    const after = value.slice(cursor);
    const replacement = `@${opt.token} `;
    const next = `${before}${replacement}${after}`;
    onChange(next);
    setDropdown({ open: false, query: '', startIndex: -1, activeIndex: 0 });
    // restore caret after the insertion, on next tick
    requestAnimationFrame(() => {
      if (!ref.current) return;
      const pos = (before + replacement).length;
      ref.current.focus();
      ref.current.setSelectionRange(pos, pos);
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!dropdown.open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDropdown((d) => ({
        ...d,
        activeIndex: (d.activeIndex + 1) % filtered.length
      }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDropdown((d) => ({
        ...d,
        activeIndex: (d.activeIndex - 1 + filtered.length) % filtered.length
      }));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertAlias(filtered[dropdown.activeIndex]);
    } else if (e.key === 'Escape') {
      setDropdown((d) => ({ ...d, open: false }));
    }
  }

  return (
    <div className="mention-textarea-wrap">
      <textarea
        ref={ref}
        id={id}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={className ?? 'form-input form-textarea'}
      />
      {dropdown.open && filtered.length > 0 ? (
        <ul className="mention-dropdown" role="listbox" aria-label="Mention users">
          {filtered.map((opt, i) => (
            <li
              key={opt.id}
              role="option"
              aria-selected={i === dropdown.activeIndex}
              className={`mention-option ${i === dropdown.activeIndex ? 'active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault(); // keep textarea focus
                insertAlias(opt);
              }}
            >
              <span className="mention-option-token">@{opt.token}</span>
              <span className="mention-option-name">{opt.name}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
